import { z } from 'zod'
import { DAY_PARAM_PATTERN } from './meeting-transcripts'
import {
  MEETING_HUBSPOT_LINK_STATES,
  MEETING_OUTCOME_VIEWS,
  MEETING_TRANSCRIPT_FILTERS,
  MEETING_TRANSCRIPT_SOURCES,
} from '@/types/meetingTranscripts.types'

/**
 * sc-863. The Meeting transcripts page's search params: which tab, which
 * sheets are open, and the filters and page of the Meetings and Outcomes tabs. Kept out of the
 * route file so the hooks that read and write them (and the legacy Zoom route
 * that redirects here) can be tested against the real schema.
 */

export const MEETING_TRANSCRIPTS_TABS = [
  'overview',
  'meetings',
  'outcomes',
  'backfill',
  'webhooks',
] as const
/** A day as the API takes it. A malformed one falls away rather than blanking the page. */
const dayParam = z.string().regex(DAY_PARAM_PATTERN).optional().catch(undefined)

export const meetingTranscriptsSearchSchema = z.object({
  tab: z.enum(MEETING_TRANSCRIPTS_TABS).optional().default('overview'),
  // Bad ids fall away instead of blanking the page: the API would answer 400.
  meetingId: z.string().uuid().optional().catch(undefined),
  transcriptId: z.string().uuid().optional().catch(undefined),
  /**
   * The HubSpot meeting whose outcome details are open (our mirror's id, not a
   * uuid), over the Outcomes tab. Free text: the API answers 404 for an
   * unknown one and the sheet says so.
   */
  outcomeId: z.string().trim().min(1).max(100).optional().catch(undefined),

  // One source at a time. On the Meetings tab it filters the list; on the
  // Backfill tab it is the source the form starts. Switching tabs drops it.
  source: z.enum(MEETING_TRANSCRIPT_SOURCES).optional().catch(undefined),

  // Meetings tab only (switching tabs drops them). They live in the URL so a
  // reload, a link, or Back from a client page keeps the list as it was.
  /** Comma-joined link statuses, the app's multi-select codec: `MATCHED,UNMATCHED`. */
  linkStatus: z.string().optional().catch(undefined),
  /**
   * Comma-joined categories of call, the same codec: `SALES,SERVICE`. The
   * Outcomes tab reads the same param, keeping only those two.
   */
  category: z.string().optional().catch(undefined),
  /** One HubSpot meeting link state: `NONE`, `AUTO`, `CONFIRMED` or `DISMISSED`. */
  hubspotLink: z.enum(MEETING_HUBSPOT_LINK_STATES).optional().catch(undefined),
  /**
   * Only calls whose client is known and that have no HubSpot meeting: what
   * waits for a person. The router reads `?needsMeeting=true` as a boolean; the
   * text "true" is accepted too, and anything else falls away. It replaces
   * `hubspotLink` (the two cannot both hold), so only `true` is ever written.
   */
  needsMeeting: z
    .union([
      z.boolean(),
      z.enum(['true', 'false']).transform((v) => v === 'true'),
    ])
    .optional()
    .catch(undefined),
  /**
   * One HubSpot meeting type, by the label `GET meeting-types` lists. Free
   * text, capped as the API caps it; a label that looks like a number reads
   * back as a number when the link was typed by hand, so both must work.
   */
  meetingType: z
    .union([z.string().trim().min(1).max(200), z.number().transform(String)])
    .optional()
    .catch(undefined),
  transcriptStatus: z
    .enum(MEETING_TRANSCRIPT_FILTERS)
    .optional()
    .catch(undefined),
  /** `YYYY-MM-DD`, inclusive. */
  startFrom: dayParam,
  startTo: dayParam,
  // A number-looking search (a meeting number) reads back as a number when the
  // link was typed by hand; ours are quoted, but both must work.
  search: z
    .union([z.string(), z.number().transform(String)])
    .optional()
    .catch(undefined),
  /**
   * Comma-joined tag ids, the same codec: `<id>,<id>`. Calls carrying any of
   * them. Ids that are not ids fall away when it is read (see `parseTagParam`).
   */
  tag: z.string().optional().catch(undefined),
  /**
   * What was said: the transcript search, apart from `search` (topic, host,
   * client, meeting number). Free text with the API's cap; it also tells the
   * transcript viewer which words to light up, so it stays in the URL while a
   * meeting and its transcript are open over the list. A number-looking one
   * reads back as a number, so both must work.
   */
  q: z
    .union([z.string().max(200), z.number().transform(String)])
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),

  // Outcomes tab only (switching tabs drops them), with `category` and `page`
  // above. Absent means the tab's start: the meetings that need a person.
  /** Which meetings the tab lists: `NEEDS_PERSON` (the default), `DISAGREES` or `ALL`. */
  view: z.enum(MEETING_OUTCOME_VIEWS).optional().catch(undefined),
  /** Comma-joined, what Rothology says: `COMPLETED,NO_SHOW,UNKNOWN`. */
  outcome: z.string().optional().catch(undefined),
  /** `YYYY-MM-DD`, inclusive, on the meeting's END (the Meetings tab's `startFrom` is on its start). */
  endFrom: dayParam,
  endTo: dayParam,
})
